import { useEffect, useRef } from 'react';

const healthTopics = [
  {
    icon: '🥗',
    title: 'Healthy Eating',
    description:
      'Discover balanced nutrition tips, healthy recipes, meal-planning ideas and guidance on choosing nutritious foods for a healthier lifestyle.',
  },
  {
    icon: '🏃',
    title: 'Fitness and Exercise',
    description:
      'Explore beginner-friendly workout plans, home exercises, stretching routines, strength training and practical ways to stay active every day.',
  },
  {
    icon: '🧠',
    title: 'Mental Health and Wellness',
    description:
      'Learn techniques to reduce stress, improve emotional well-being, practise mindfulness and build healthy coping strategies for everyday life.',
  },
  {
    icon: '🌙',
    title: 'Sleep and Recovery',
    description:
      'Find helpful advice on improving sleep quality, creating healthy bedtime routines and understanding the importance of rest for overall health.',
  },
  {
    icon: '🛡️',
    title: 'Disease Prevention',
    description:
      'Read about preventive healthcare, vaccinations, regular health screenings, heart health, diabetes awareness and long-term wellness.',
  },
  {
    icon: '☀️',
    title: 'Healthy Lifestyle Tips',
    description:
      'Gain practical advice on hydration, weight management, healthy habits, quitting smoking, reducing sugar intake and balanced living.',
  },
  {
    icon: '📊',
    title: 'Health Tools and Resources',
    description:
      'Access BMI and calorie calculators, water-intake guides, wellness checklists and educational articles to support your health journey.',
  },
];

const reasons = [
  'Reliable, easy-to-understand health information',
  'Practical tips you can apply in daily life',
  'Regularly updated wellness articles',
  'Resources suitable for people of all ages',
  'A focus on prevention and healthy living',
];

function HealthyLifeGuide() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const elements = section.querySelectorAll('[data-health-reveal]');
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return undefined;
    }

    section.classList.add('is-motion-ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -40px' });

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="health-guide"
      id="health-guide"
      aria-labelledby="health-guide-title"
      ref={sectionRef}
    >
      <div className="container">
        <div className="health-guide-intro health-reveal" data-health-reveal>
          <div>
            <span className="eyebrow">Healthy Life Guide</span>
            <h2 id="health-guide-title">Your trusted partner for better health.</h2>
          </div>
          <p>
            Welcome to Healthy Life Guide, your reliable source for practical
            health and wellness information. We help individuals and families
            make informed decisions that support a healthier lifestyle—from
            nutrition and physical activity to stress management and disease
            prevention.
          </p>
        </div>

        <div className="health-mission health-reveal" data-health-reveal>
          <div className="health-mission-icon" aria-hidden="true">❤️</div>
          <div>
            <span>Our mission</span>
            <h3>Good health begins with knowledge and healthy daily habits.</h3>
            <p>
              Our goal is to provide clear, easy-to-understand and evidence-based
              health information that empowers people to take charge of their
              well-being.
            </p>
          </div>
        </div>

        <div className="health-topics-heading health-reveal" data-health-reveal>
          <div>
            <span className="eyebrow">Explore wellness</span>
            <h3>What you’ll find on our website</h3>
          </div>
          <p>Practical guidance for your physical, mental and emotional well-being.</p>
        </div>

        <div className="health-topics-grid">
          {healthTopics.map((topic, index) => (
            <article
              className="health-topic-card health-reveal"
              data-health-reveal
              key={topic.title}
              style={{ '--reveal-delay': `${(index % 3) * 90}ms` }}
            >
              <span className="health-topic-icon" aria-hidden="true">
                {topic.icon}
              </span>
              <h4>{topic.title}</h4>
              <p>{topic.description}</p>
            </article>
          ))}
        </div>

        <div className="health-bottom-grid">
          <article className="health-reasons health-reveal" data-health-reveal>
            <span className="eyebrow eyebrow-light">Why choose us?</span>
            <h3>Guidance designed for real life.</h3>
            <ul>
              {reasons.map((reason) => <li key={reason}>{reason}</li>)}
            </ul>
          </article>

          <div className="health-commitment">
            <article className="health-reveal" data-health-reveal>
              <span className="eyebrow">Our commitment</span>
              <h3>Healthy changes that last.</h3>
              <p>
                We are committed to promoting healthy lifestyles through
                educational content and practical guidance. Our aim is to help
                you build sustainable habits that improve your physical, mental
                and emotional well-being.
              </p>
            </article>

            <aside
              className="medical-disclaimer health-reveal"
              data-health-reveal
              aria-labelledby="medical-disclaimer-title"
            >
              <span aria-hidden="true">i</span>
              <div>
                <h3 id="medical-disclaimer-title">Medical disclaimer</h3>
                <p>
                  The information on this website is for educational and
                  informational purposes only and is not a substitute for
                  professional medical advice, diagnosis or treatment. Always
                  consult a qualified healthcare professional about medical
                  concerns or before making significant changes to your diet,
                  exercise routine or medications. Never ignore professional
                  medical advice because of information you have read here.
                </p>
              </div>
            </aside>
          </div>
        </div>

        <div className="health-journey-callout health-reveal" data-health-reveal>
          <div>
            <span>Start with one small choice</span>
            <h3>Join us on your health journey.</h3>
            <p>
              Small healthy choices made consistently can lead to lasting
              improvements in your quality of life. Explore our health guides,
              wellness resources and practical tips for a healthier, happier future.
            </p>
          </div>
          <a className="btn btn-accent" href="#health-guide-title">
            Explore the guide <span aria-hidden="true">↑</span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default HealthyLifeGuide;
